import {
  HeadContent,
  Scripts,
  createRootRouteWithContext,
} from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy } from 'react'

import appCss from '../styles.css?url'
import NotFoundPage from '#/pages/NotFoundPage'
import ErrorPage from '#/pages/ErrorPage'

const Devtools = import.meta.env.DEV
  ? lazy(async () => {
      const [devtools, routerDevtools] = await Promise.all([
        import('@tanstack/react-devtools'),
        import('@tanstack/react-router-devtools'),
      ])
      return {
        default: () => (
          <devtools.TanStackDevtools
            config={{
              position: 'bottom-right',
            }}
            plugins={[
              {
                name: 'Tanstack Router',
                render: <routerDevtools.TanStackRouterDevtoolsPanel />,
              },
            ]}
          />
        ),
      }
    })
  : () => null


export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
}>()({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        name: 'google-site-verification',
        content: 'JgIpHh9fOAAfapyOMLNEIOXcl1j0wBFsjrPotRpNG9s',
      },
      {
        title: "Wa'allamah | Online Quran & Islamic Academy",
      },
      {
        name: 'description',
        content:
          'Empowering learners worldwide to connect with the Qur’an through authentic, personalized, and engaging Islamic education. Learn the Qur’an. Live by its Guidance.',
      },
      {
        name: 'keywords',
        content:
          "Wa'allamah, Waallamah, وعلمك, أكاديمية وعلمك, منصة وعلمك, Quran Academy, Learn Quran Online, Tajweed Classes, Islamic Studies, Arabic Language, Online Quran Tutors, Quran for Kids, Hifz Program, تعلّم القرآن, تجويد, لغة عربية",
      },
      {
        name: 'author',
        content: "Wa'allamah Institute",
      },
      {
        property: 'og:title',
        content: "Wa'allamah | Online Quran & Islamic Academy",
      },
      {
        property: 'og:description',
        content:
          'Empowering learners worldwide to connect with the Qur’an through authentic, personalized, and engaging Islamic education.',
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:site_name',
        content: "Wa'allamah",
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:title',
        content: "Wa'allamah | Online Quran & Islamic Academy",
      },
      {
        name: 'twitter:description',
        content:
          'Empowering learners worldwide to connect with the Qur’an through authentic, personalized, and engaging Islamic education.',
      },
      {
        name: 'theme-color',
        content: '#ffffff',
        media: '(prefers-color-scheme: light)',
      },
      {
        name: 'theme-color',
        content: '#0f172a',
        media: '(prefers-color-scheme: dark)',
      },
    ],
    links: [
      {
        rel: 'preload',
        href: appCss,
        as: 'style',
      },
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        href: 'https://fonts.gstatic.com/s/philosopher/v21/vEFI2_5QCwIS4_Dhez5jcWjValgb8tc.woff2',
        as: 'font',
        type: 'font/woff2',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        href: 'https://fonts.gstatic.com/s/poppins/v24/pxiEyp8kv8JHgFVrJJfecg.woff2',
        as: 'font',
        type: 'font/woff2',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preconnect',
        href: 'https://api.wa-allamah.com',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'dns-prefetch',
        href: 'https://api.wa-allamah.com',
      },
      {
        rel: 'icon',
        type: 'image/x-icon',
        href: '/favicon.ico?v=2',
      },
      {
        rel: 'shortcut icon',
        type: 'image/x-icon',
        href: '/favicon.ico?v=2',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '96x96',
        href: '/favicon-96x96.png?v=2',
      },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/apple-touch-icon.png?v=2',
      },
      {
        rel: 'manifest',
        href: '/site.webmanifest?v=2',
      },
      {
        rel: 'stylesheet',
        href: appCss,
      },
    ],
  }),
  notFoundComponent: () => <NotFoundPage />,
  errorComponent: ({ error, reset }) => (
    <ErrorPage error={error} reset={reset} />
  ),
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  const { queryClient } = Route.useRouteContext()

  return (
    <html lang="en">
      <head>
        <HeadContent />
        {/* Google Analytics - Deferred Load */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-C7ZYN8C4HT');

              function loadGtag() {
                if (window._gtagLoaded) return;
                window._gtagLoaded = true;
                var s = document.createElement('script');
                s.async = true;
                s.src = 'https://www.googletagmanager.com/gtag/js?id=G-C7ZYN8C4HT';
                document.head.appendChild(s);
              }

              var events = ['pointerdown', 'touchstart', 'scroll', 'keydown'];
              events.forEach(function(e) {
                window.addEventListener(e, loadGtag, { once: true, passive: true });
              });

              if (document.readyState === 'complete') {
                setTimeout(loadGtag, 2500);
              } else {
                window.addEventListener('load', function() {
                  setTimeout(loadGtag, 2500);
                });
              }
            `,
          }}
        />
      </head>
      <body
        className="font-sans antialiased wrap-anywhere selection:bg-[rgba(79,184,178,0.24)]"
        suppressHydrationWarning
      >
        <QueryClientProvider client={queryClient}>
          {children}
        </QueryClientProvider>
        {import.meta.env.DEV && (
          <Suspense fallback={null}>
            <Devtools />
          </Suspense>
        )}
        <Scripts />
      </body>
    </html>
  )
}
